import { toast } from "react-toastify";
import { isAxiosError } from "axios";

import { useUtils } from "../contexts/UtilsContext";
import { IDefaultCreateResponse } from "./integrations/types";
import {
    checkUserExists,
    IPasswordRecoveryRequest,
    IRegisterUserRequest,
    IResetPassword,
    IValidateEmailRequest,
    registerUser,
    resetPassword,
    sendPasswordRecovery,
    sendVerificationEmail,
    validateEmail,
} from "./integrations/users";

interface IUseUsers {
    checkIfUserExists: (data: {
        email?: string;
        username?: string;
    }) => Promise<boolean | undefined>;
    createPublicUser: (data: IRegisterUserRequest) => Promise<IDefaultCreateResponse | undefined>;
    requestPasswordRecovery: (data: IPasswordRecoveryRequest) => Promise<boolean>;
    resetUserPassword: (data: IResetPassword) => Promise<boolean>;
    requestVerificationEmail: () => Promise<boolean>;
    validateUserEmail: (data: IValidateEmailRequest) => Promise<boolean>;
}

function getApiErrorDetail(error: unknown) {
    if (!isAxiosError(error)) {
        return;
    }

    const detail = error.response?.data?.detail;

    if (typeof detail === "string") {
        return detail;
    }

    if (detail && typeof detail === "object") {
        const firstMessage = Object.values(detail)[0];

        if (typeof firstMessage === "string") {
            return firstMessage;
        }
    }
}

export default function useUsers(): IUseUsers {
    const { setIsLoading } = useUtils();

    const checkIfUserExists = async (data: { email?: string; username?: string }) => {
        try {
            const response = await checkUserExists(data);
            return response.data.userExists;
        } catch (_error: unknown) {
            return;
        }
    };

    const createPublicUser = async (data: IRegisterUserRequest) => {
        try {
            setIsLoading(true);
            const response = await registerUser(data);
            toast.success("Conta criada com sucesso.");
            return response.data;
        } catch (error: unknown) {
            toast.error(getApiErrorDetail(error) || "Tivemos um erro ao criar a conta.");
            return;
        } finally {
            setIsLoading(false);
        }
    };

    const requestPasswordRecovery = async (data: IPasswordRecoveryRequest) => {
        try {
            setIsLoading(true);
            await sendPasswordRecovery(data);
            return true;
        } catch (error: unknown) {
            toast.error(
                getApiErrorDetail(error) || "Tivemos um problema ao solicitar a recuperacao."
            );
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const resetUserPassword = async (data: IResetPassword) => {
        try {
            setIsLoading(true);
            await resetPassword(data);
            toast.success("Senha redefinida com sucesso.");
            return true;
        } catch (error: unknown) {
            if (isAxiosError(error)) {
                const status = error.response?.status ?? 0;
                if (status >= 400 && status < 500) {
                    toast.error(
                        getApiErrorDetail(error) ||
                            "Houve um problema para redefinir a senha. Peca para o administrador gerar novamente uma URL para definir a senha."
                    );
                    return false;
                }
                if (status >= 500) {
                    toast.error(
                        "Houve um problema para redefinir a senha. Tente novamente mais tarde."
                    );
                    return false;
                }
                toast.error("Tivemos um erro ao chamar a API.");
                return false;
            }
            toast.error("Tivemos um erro ao redefinir a senha.");
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const requestVerificationEmail = async () => {
        try {
            setIsLoading(true);
            await sendVerificationEmail();
            toast.success("Codigo enviado para o seu email.");
            return true;
        } catch (error: unknown) {
            toast.error(
                getApiErrorDetail(error) || "Tivemos um problema ao enviar o codigo de validacao."
            );
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const validateUserEmail = async (data: IValidateEmailRequest) => {
        try {
            setIsLoading(true);
            await validateEmail(data);
            toast.success("Email validado com sucesso.");
            return true;
        } catch (error: unknown) {
            toast.error(
                getApiErrorDetail(error) || "Tivemos um problema ao validar o codigo informado."
            );
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        checkIfUserExists,
        createPublicUser,
        requestPasswordRecovery,
        resetUserPassword,
        requestVerificationEmail,
        validateUserEmail,
    };
}
